import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Cpu, Home as HomeIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/content/homepage`).then(res => {
      if (res.data && Object.keys(res.data).length > 0) setPageContent(res.data);
    }).catch(() => {});
  }, []);

  const getText = (field, fallback) => {
    const val = pageContent?.[field];
    if (!val) return fallback;
    if (typeof val === 'object') return val[lang] || val.en || fallback;
    return val || fallback;
  };

  const gamingImage = pageContent?.gaming_image || null;
  const officeImage = pageContent?.office_image || null;
  const gamingCardLogo = pageContent?.gaming_card_logo || null;
  const officeCardLogo = pageContent?.office_card_logo || null;

  useEffect(() => { document.title = 'VIYERO | Ergonomik Çözümler - Gaming & Home Office'; return () => { document.title = 'VIYERO | Ergonomic Solutions'; }; }, []);
  
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {getText('hero_title', t('home.heroTitle'))}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {getText('hero_description', t('home.heroDesc'))}
            </p>
          </div>

          {/* Product Categories */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Gaming Category */}
            <Link to="/gaming" className="group">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-lg hover:shadow-2xl transition-all duration-500">
                {gamingImage ? (
                <img
                  src={gamingImage}
                  alt="VIYERO Gaming"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                ) : (
                <div className="w-full h-full bg-gray-800 animate-pulse" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />

                {/* VIYERO Logo Bottom Right */}
                {gamingCardLogo && (
                <div className="absolute bottom-6 right-6">
                  <img
                    src={gamingCardLogo}
                    alt="VIYERO"
                    className="h-5 md:h-6 w-auto opacity-40 filter brightness-200"
                  />
                </div>
                )}

                <div className="absolute bottom-6 left-6 right-24" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.5)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    {pageContent?.gaming_label_icon
                      ? <img src={pageContent.gaming_label_icon} alt="" className="h-5 w-5" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }} />
                      : <Cpu className="h-5 w-5 text-purple-400" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }} />
                    }
                    <span className="text-sm font-medium text-purple-400 uppercase tracking-wider">
                      {getText('gaming_label', t('home.gamingLabel'))}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {getText('gaming_title', t('home.gamingTitle'))}
                  </h2>
                  <p className="text-gray-300 text-sm md:text-base mb-4">
                    {getText('gaming_description', t('home.gamingDesc'))}
                  </p>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white group-hover:translate-x-1 transition-transform">
                    {getText('gaming_cta', t('home.gamingCTA'))}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Link>

            {/* Office Category */}
            <Link to="/office" className="group">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-lg hover:shadow-2xl transition-all duration-500">
                {officeImage ? (
                <img
                  src={officeImage}
                  alt="VIYERO Office"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                ) : (
                <div className="w-full h-full bg-gray-800 animate-pulse" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />

                {/* VIYERO Logo Bottom Right */}
                {officeCardLogo && (
                <div className="absolute bottom-6 right-6">
                  <img
                    src={officeCardLogo}
                    alt="VIYERO"
                    className="h-5 md:h-6 w-auto opacity-40 filter brightness-200"
                  />
                </div>
                )}

                <div className="absolute bottom-6 left-6 right-24" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.5)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    {pageContent?.office_label_icon
                      ? <img src={pageContent.office_label_icon} alt="" className="h-5 w-5" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }} />
                      : <HomeIcon className="h-5 w-5 text-blue-400" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }} />
                    }
                    <span className="text-sm font-medium text-blue-400 uppercase tracking-wider">
                      {getText('office_label', t('home.officeLabel'))}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    {getText('office_title', t('home.officeTitle'))}
                  </h2>
                  <p className="text-gray-300 text-sm md:text-base mb-4">
                    {getText('office_description', t('home.officeDesc'))}
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white group-hover:translate-x-1 transition-transform">
                    {getText('office_cta', t('home.officeCTA'))}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Promise */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-lg text-gray-600">
              {getText('why_choose_description', t('home.whyChooseDesc'))}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-purple-600 rounded-lg" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{getText('value_1_title', t('home.value1Title'))}</h3>
              <p className="text-gray-600">
                {getText('value_1_description', t('home.value1Desc'))}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{getText('value_2_title', t('home.value2Title'))}</h3>
              <p className="text-gray-600">
                {getText('value_2_description', t('home.value2Desc'))}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-gray-800 rounded-lg" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{getText('value_3_title', t('home.value3Title'))}</h3>
              <p className="text-gray-600">
                {getText('value_3_description', t('home.value3Desc'))}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('cta_title', t('home.ctaTitle'))}
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              {getText('cta_description', t('home.ctaDesc'))}
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100">
                {t('home.ctaButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
